import { type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DividerOrientation = 'horizontal' | 'vertical';

/**
 * Spacing tokens 1–10, mapping to `--spacing-1` … `--spacing-10`.
 *
 * | Token | Value  | px  |
 * |-------|--------|-----|
 * | 1     | 0.25rem| 4   |
 * | 2     | 0.5rem | 8   |
 * | 3     | 0.75rem| 12  |
 * | 4     | 1rem   | 16  |
 * | 5     | 1.5rem | 24  |
 * | 6     | 2rem   | 32  |
 * | 7     | 2.5rem | 40  |
 * | 8     | 3rem   | 48  |
 * | 9     | 3.5rem | 56  |
 * | 10    | 4rem   | 64  |
 */
export type SpacingToken = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/**
 * Props for the Divider component.
 *
 * `className` is intentionally omitted — Divider is a sealed design system
 * component. Orientation, thickness, and spacing are controlled via named
 * props.
 */
export interface DividerProps extends Omit<HTMLAttributes<HTMLHRElement>, 'className' | 'style'> {
  /**
   * Whether the divider runs horizontally (full width) or vertically (full
   * height of its flex container).
   *
   * @default "horizontal"
   */
  orientation?: DividerOrientation;
  /**
   * Optional spacing applied on both sides of the divider using Primer spacing
   * tokens (`--spacing-1` … `--spacing-10`).
   *
   * - Horizontal: adds `my-N` (top/bottom margin)
   * - Vertical: adds `mx-N` (left/right margin)
   */
  spacing?: SpacingToken;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Divider component.
 *
 * A thin 1px rule used to visually separate sections of content.
 * Colour is always `bg-divider` (mapped to `--color-neutral-300` in light
 * mode, `--color-neutral-900` in dark mode).
 *
 * ### Orientation
 * - **horizontal** (default) — spans the full width of its container;
 *   `height: 1px`. Place inside a block or flex-column container.
 * - **vertical** — spans the full height of its flex-row container;
 *   `width: 1px`. The parent must be `display: flex` with `align-items:
 *   stretch` (or equivalent) for the divider to fill the row height.
 *
 * ### Spacing
 * The optional `spacing` prop accepts a token value `1–10` and applies margin
 * on both sides perpendicular to the divider line via Tailwind utility classes
 * (`my-N` for horizontal, `mx-N` for vertical). In Tailwind v4 these map
 * directly to `--spacing-N` custom properties — no inline styles needed.
 *
 * ### Accessibility
 * Rendered as `<hr>` with `role="separator"` and `aria-orientation` set
 * automatically from the `orientation` prop.
 *
 * @example
 * // Default horizontal rule
 * <Divider />
 *
 * @example
 * // Horizontal with spacing-3 above and below
 * <Divider spacing={3} />
 *
 * @example
 * // Vertical divider inside a flex row
 * <div className="flex items-stretch">
 *   <span>Left</span>
 *   <Divider orientation="vertical" spacing={2} />
 *   <span>Right</span>
 * </div>
 */
export function Divider({ orientation = 'horizontal', spacing, ...rest }: DividerProps) {
  const isHorizontal = orientation === 'horizontal';

  const spacingClass =
    spacing !== undefined ? (isHorizontal ? `my-${spacing}` : `mx-${spacing}`) : undefined;

  return (
    <hr
      role="separator"
      aria-orientation={orientation}
      aria-hidden="true"
      className={cn(
        'border-0 bg-divider flex-shrink-0',
        isHorizontal ? 'w-full h-px' : 'h-full w-px',
        spacingClass
      )}
      {...rest}
    />
  );
}

export default Divider;
